import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { installDevTelemetry, buildRelatorioErrosClipboard } from "@/shared/dev/devDiagnosticsTelemetry";
import { isBenignWindowError } from "@/shared/lib/benignErrors";

if (import.meta.env.DEV) {
  installDevTelemetry();
}

// Sentry só inicializa com DSN configurado (evita erros em dev sem .env)
const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  const isDev = import.meta.env.DEV;
  Sentry.init({
    dsn,
    release: __SENTRY_RELEASE__,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay desativado em DEV para reduzir violations (rrweb) e carga no console
      ...(isDev ? [] : [Sentry.replayIntegration()]),
    ],
    tracesSampleRate: 0.02,
    replaysOnErrorSampleRate: isDev ? 0 : 0.5,
    replaysSessionSampleRate: 0,
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (isBenignWindowError(error)) return null;
      const msg = event.message ?? event.exception?.values?.[0]?.value ?? "";
      if (typeof msg === "string" && isBenignWindowError(msg)) return null;
      return event;
    },
  });
  // Teste de pipeline removido para evitar poluir Sentry. Use Sentry Issues para validar.
}

// Show errors visually on page even if React crashes
function showFatalError(msg: string, stack?: string) {
  const root = document.getElementById("root");
  if (!root) return;
  root.replaceChildren();

  const wrap = document.createElement("div");
  wrap.style.cssText =
    "min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;font-family:system-ui,sans-serif;background:#f9f6f0;";

  const card = document.createElement("div");
  card.style.cssText = "max-width:560px;text-align:center;width:100%;";

  const h1 = document.createElement("h1");
  h1.textContent = "Erro no Sistema";
  h1.style.cssText = "font-size:1.5rem;color:#333;margin:0 0 0.75rem;";

  const p = document.createElement("p");
  p.textContent = "Ocorreu um erro inesperado.";
  p.style.cssText = "color:#666;font-size:0.9rem;margin:0 0 1rem;";

  const details = document.createElement("details");
  details.style.cssText =
    "text-align:left;background:#f0ece6;padding:1rem;border-radius:8px;font-size:0.75rem;color:#444;margin-bottom:0.75rem;border:1px solid #e0dcd4;";
  const summary = document.createElement("summary");
  summary.textContent = "Detalhes técnicos";
  summary.style.cssText = "cursor:pointer;font-weight:600;";
  const pre = document.createElement("pre");
  pre.style.cssText = "white-space:pre-wrap;word-break:break-word;margin:0.5rem 0 0;";
  pre.textContent = stack ? `${msg}\n\n--- stack ---\n${stack}` : msg;
  details.append(summary, pre);

  const btnRow = document.createElement("div");
  btnRow.style.cssText = "display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:0.75rem;";

  const btnStyle =
    "padding:0.5rem 1rem;background:#7a1c1c;color:white;border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;";
  const outlineStyle =
    "padding:0.5rem 1rem;background:transparent;color:#7a1c1c;border:1px solid #7a1c1c;border-radius:6px;cursor:pointer;font-size:0.85rem;";

  const reloadBtn = document.createElement("button");
  reloadBtn.textContent = "Recarregar";
  reloadBtn.style.cssText = btnStyle;
  reloadBtn.onclick = () => location.reload();

  const copyErrBtn = document.createElement("button");
  copyErrBtn.textContent = "Copiar erro (mensagem + stack)";
  copyErrBtn.style.cssText = outlineStyle;
  copyErrBtn.onclick = () => {
    const t = stack ? `${msg}\n\n--- stack ---\n${stack}` : msg;
    void navigator.clipboard.writeText(t).then(
      () => {
        copyErrBtn.textContent = "Copiado!";
        setTimeout(() => {
          copyErrBtn.textContent = "Copiar erro (mensagem + stack)";
        }, 2000);
      },
      () => {
        copyErrBtn.textContent = "Falha ao copiar";
      }
    );
  };

  btnRow.append(reloadBtn, copyErrBtn);

  if (import.meta.env.DEV) {
    const copyDevBtn = document.createElement("button");
    copyDevBtn.textContent = "Copiar relatório DEV (rede + console)";
    copyDevBtn.title = "Inclui fetch 4xx/5xx, falhas de rede e console — mesmo bloco da barra de diagnóstico.";
    copyDevBtn.style.cssText = outlineStyle;
    copyDevBtn.onclick = () => {
      try {
        const full = [
          "--- Erro fatal (React / window) ---",
          msg,
          stack ? `\n--- stack ---\n${stack}` : "",
          "\n\n",
          buildRelatorioErrosClipboard(),
        ].join("");
        void navigator.clipboard.writeText(full).then(
          () => {
            copyDevBtn.textContent = "Relatório DEV copiado!";
            setTimeout(() => {
              copyDevBtn.textContent = "Copiar relatório DEV (rede + console)";
            }, 2200);
          },
          () => {
            copyDevBtn.textContent = "Falha ao copiar";
          }
        );
      } catch {
        copyDevBtn.textContent = "Falha ao montar relatório";
      }
    };
    btnRow.append(copyDevBtn);
  }

  card.append(h1, p, details, btnRow);
  wrap.append(card);
  root.append(wrap);
}

// Catch unhandled promise rejections — reportar ao Sentry explicitamente
window.addEventListener("unhandledrejection", (event) => {
  console.error("[Unhandled Rejection]", event.reason);
  if (dsn && !isBenignWindowError(event.reason)) {
    Sentry.captureException(event.reason);
  }
  event.preventDefault();
});

// Catch uncaught errors — reportar ao Sentry explicitamente
window.addEventListener("error", async (event) => {
  // Ignore benign DOM manipulation errors (browser extensions)
  if (isBenignWindowError(event.error || event)) {
    event.preventDefault();
    console.warn("[Suppressed benign DOM error]", event.error?.message || event.message);
    return;
  }
  console.error("[Uncaught Error]", event.error);
  if (dsn) {
    Sentry.captureException(event.error || new Error(event.message));
    await Sentry.flush(2000); // Garante envio antes de substituir a página
  }
  const err = event.error;
  const stack = err instanceof Error ? err.stack : undefined;
  showFatalError(err instanceof Error ? err.message : event.message || "Erro desconhecido", stack);
});

try {
  createRoot(document.getElementById("root")!).render(<App />);
} catch (err: unknown) {
  console.error("[Render Error]", err);
  const msg = err instanceof Error ? err.message : "Erro ao iniciar aplicação";
  const stack = err instanceof Error ? err.stack : undefined;
  if (dsn) {
    Sentry.captureException(err);
    Sentry.flush(2000)
      .then(() => showFatalError(msg, stack))
      .catch(() => showFatalError(msg, stack));
  } else {
    showFatalError(msg, stack);
  }
}
